import { useTheme } from '@chronosphereio/chrono-ui';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { formatValue, GenericThresholdOptions, StepOptions, type UnitOptions } from '@/core';
import { DependencyLink } from './types';

/** Layout knobs shared by `build-cluster-layout` and `DependencyMapCluster`. */
export interface DependencyMapLayoutConfig {
  /** Horizontal distance between columns (px). */
  columnSpacingPx?: number;
  /** Vertical distance between nodes within a column (px). */
  nodeSpacingPx?: number;
  /** Per-column cap. Lowest-traffic excess collapses into one "Other (N)" node. */
  maxNodesPerColumn?: number;
}

/**
 * One step in a threshold ladder; matched when `value >= step.value`.
 *
 * Aliased to viz-framework's `StepOptions` so this map shares threshold tooling
 * with dashboard panel plugins. `operator` is ignored — every step is treated
 * as `>=`, since point-equality doesn't compose with the priority ordering used
 * for link z-order and node coloring.
 */
export type DependencyMapThresholdStep = StepOptions;

/**
 * Color-by-value rules for link strokes. `base` is the fallback; `steps[]`
 * carve out value ranges. `mode: 'Percent'` + `max` lets consumers express
 * steps as percentages of `max`. `default_color` is a deprecated alias for
 * `base.color`.
 *
 * Aliased to viz-framework's `GenericThresholdOptions` to match dashboard
 * panel plugins (and inherits their snake_case keys).
 */
export type DependencyMapThresholds = GenericThresholdOptions<StepOptions>;

/**
 * Names for the kind of entity this map represents. Drives user-facing strings
 * ("All <plural>", "Filter by <singular>") so the component isn't tied to any
 * one entity kind.
 */
export interface DependencyMapNodeType {
  singular: string;
  plural: string;
}

/**
 * Per-instance config. Lives in context so descendants can read it without
 * prop-drilling. Eventual target shape for the dashboard panel extraction.
 */
export interface DependencyMapConfig {
  layout?: DependencyMapLayoutConfig;
  thresholds?: DependencyMapThresholds;
  /** Number formatting (`UnitOptions` discriminated union). Defaults to decimal. */
  unit?: UnitOptions;
  /** Unit label appended in tooltips/legend, e.g. `req/sec`, `ms`, `%`. */
  metricLabel?: string;
  /** Entity-kind names. Defaults to `node` / `nodes`. */
  nodeType?: DependencyMapNodeType;
}

/** Resolved step: guaranteed `color`, absolute `value` (percent already converted). */
export interface ResolvedDependencyMapThresholdStep extends StepOptions {
  color: string;
}

/** Resolved thresholds: `base.color` filled in, `steps` sorted ascending. */
export interface ResolvedDependencyMapThresholds {
  base: { color: string; name?: string };
  steps: ResolvedDependencyMapThresholdStep[];
}

/** `DependencyMapConfig` with all defaults applied. Read via `useDependencyMapConfig`. */
export interface ResolvedDependencyMapConfig {
  layout: Required<DependencyMapLayoutConfig>;
  thresholds: ResolvedDependencyMapThresholds;
  unit: UnitOptions;
  metricLabel: string;
  nodeType: DependencyMapNodeType;
}

const DEFAULT_LAYOUT: Required<DependencyMapLayoutConfig> = {
  columnSpacingPx: 150,
  nodeSpacingPx: 28,
  maxNodesPerColumn: 24,
};

const DEFAULT_NODE_TYPE: DependencyMapNodeType = { singular: 'node', plural: 'nodes' };

const DEFAULT_UNIT: UnitOptions = { kind: 'Decimal' };

/**
 * Theme-derived fallbacks. Built per-render so the threshold base color tracks
 * the theme; the rest of resolution stays pure.
 */
export interface DependencyMapConfigDefaults {
  baseThresholdColor: string;
}

export const resolveThresholds = (
  input: DependencyMapThresholds | undefined,
  defaults: DependencyMapConfigDefaults
): ResolvedDependencyMapThresholds => {
  if (!input) return { base: { color: defaults.baseThresholdColor }, steps: [] };

  // `default_color` is a deprecated alias kept for backward compatibility.
  const baseColor = input.base?.color ?? input.default_color ?? defaults.baseThresholdColor;

  // Percent mode without a `max` is uninterpretable; fall back to absolute.
  const percentMax = input.mode === 'Percent' && typeof input.max === 'number' ? input.max : null;

  const steps = (input.steps ?? [])
    .map<ResolvedDependencyMapThresholdStep>((step) => ({
      ...step,
      value: percentMax === null ? step.value : (step.value / 100) * percentMax,
      color: step.color ?? baseColor,
    }))
    .sort((a, b) => a.value - b.value);
  return {
    base: { color: baseColor, name: input.base?.name },
    steps,
  };
};

/**
 * Shallow-merges user config over defaults per-section. `defaults` carries
 * theme-derived fallbacks so this function stays pure and testable.
 */
export const resolveDependencyMapConfig = (
  config: DependencyMapConfig,
  defaults: DependencyMapConfigDefaults
): ResolvedDependencyMapConfig => ({
  layout: { ...DEFAULT_LAYOUT, ...config.layout },
  thresholds: resolveThresholds(config.thresholds, defaults),
  unit: config.unit ?? DEFAULT_UNIT,
  metricLabel: config.metricLabel ?? '',
  nodeType: config.nodeType ?? DEFAULT_NODE_TYPE,
});

/**
 * Highest step idx with `value >= step.value`, or `-1` for "below all steps".
 * Assumes `steps` is sorted ascending (guaranteed by `resolveDependencyMapConfig`).
 */
export const matchThresholdStep = (value: number, thresholds: ResolvedDependencyMapThresholds): number => {
  let idx = -1;

  thresholds.steps.forEach((step, i) => {
    if (value >= step.value) idx = i;
  });

  return idx;
};

/** Color for a value, falling back to `base.color`. */
export const colorForValue = (value: number, thresholds: ResolvedDependencyMapThresholds): string => {
  const idx = matchThresholdStep(value, thresholds);

  return idx === -1 || !thresholds.steps[idx] ? thresholds.base.color : thresholds.steps[idx].color;
};

/** `colorForValue` for a pre-computed step idx (`-1` means base). */
export const colorForStepIdx = (idx: number, thresholds: ResolvedDependencyMapThresholds): string => {
  const step = thresholds.steps[idx];
  return idx === -1 || !step ? thresholds.base.color : step.color;
};

/**
 * Lowest/highest matched step idx among the link's underlying values. Regular
 * links collapse to `min === max`; aggregates with mixed-step members surface
 * `min !== max`, which drives the paired-stripe split. `-1` means "base color".
 */
export const extremaStepIdxForLink = (
  link: DependencyLink,
  thresholds: ResolvedDependencyMapThresholds
): { min: number; max: number } => {
  const members = link.members && link.members.length > 0 ? link.members : null;
  if (members === null) {
    const idx = matchThresholdStep(link.value, thresholds);
    return { min: idx, max: idx };
  }

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  members.forEach((member) => {
    const idx = matchThresholdStep(member.value, thresholds);
    if (idx < min) min = idx;
    if (idx > max) max = idx;
  });

  return { min, max };
};

/** Format a value with the resolved panel unit. Shared by tooltips and legend. */
export const formatDependencyMapValue = (value: number, unit: UnitOptions): string => formatValue(value, unit);

const DependencyMapConfigContext = createContext<ResolvedDependencyMapConfig | null>(null);

interface DependencyMapConfigProviderProps {
  config: DependencyMapConfig;
  children: ReactNode;
}

export const DependencyMapConfigProvider = ({ config, children }: DependencyMapConfigProviderProps) => {
  const { theme } = useTheme();
  const baseThresholdColor = theme.palette.primary.main;
  const resolved = useMemo(
    () => resolveDependencyMapConfig(config, { baseThresholdColor }),
    [config, baseThresholdColor]
  );

  return <DependencyMapConfigContext.Provider value={resolved}>{children}</DependencyMapConfigContext.Provider>;
};

/** Reads the resolved config; throws outside a provider so misuse fails loudly. */
export const useDependencyMapConfig = (): ResolvedDependencyMapConfig => {
  const config = useContext(DependencyMapConfigContext);

  if (config === null) {
    throw new Error('useDependencyMapConfig must be used inside a DependencyMapConfigProvider');
  }

  return config;
};
